import { Component, OnInit, HostListener, ElementRef, ViewChild } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { NgFor, NgIf, NgStyle, NgClass } from '@angular/common';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { FormsModule } from '@angular/forms';
import { WebsiteService } from '../../services/website.service';
import { Domain } from '../../services/website.service';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';

interface MenuItem {
  path: string;
  label: string;
  roles?: string[];
}

interface SavedDomainInfo {
  name: string;
  id: string;
  createdAt: string;
  updatedAt: string;
}

@Component({
  selector: 'app-menu',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, NgFor, NgIf, NgStyle, NgClass, FormsModule, ConfirmModalComponent],
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss']
})
export class MenuComponent implements OnInit {
  menuItems: MenuItem[] = [
    { path: '/users', label: 'Users', roles: ['superadmin', 'admin'] },
    { path: '/webshop', label: 'Webshop', roles: ['superadmin', 'admin'] },
    { path: '/knowledge-base', label: 'Help', roles: [] }
  ];

  selectedDomain: string = '';
  domains: Domain[] = [];
  showAddDomainModal = false;
  showDomainSelectionModal = false;
  showDeleteConfirmModal = false;
  domainToDelete: Domain | null = null;
  newDomainName = '';
  isSubmitting = false;
  theming: any = null;

  constructor(
    private router: Router,
    private authService: AuthService,
    private websiteService: WebsiteService,
    private firestore: Firestore
  ) {}

  async ngOnInit() {
    this.domains = await this.websiteService.getDomains();
    
    // Get saved domain data from WebsiteService
    const storedDomainData = this.websiteService.getStoredDomainData();
    const matchingDomain = this.domains.find(d => d.id === storedDomainData?.id);
    
    if (storedDomainData && matchingDomain) {
      this.selectedDomain = matchingDomain.name;
      this.onDomainChange();
    } else if (this.domains.length > 0) {
      this.selectedDomain = this.domains[0].name;
      this.onDomainChange();
    }

    // Subscribe to domain changes
    this.websiteService.currentDomain$.subscribe(async domain => {
      this.selectedDomain = domain;
      await this.loadTheming();
    });

    // Load theming on init
    await this.loadTheming();
  }

  async loadTheming() {
    const currentDomain = this.websiteService.getCurrentDomain();
    if (!currentDomain) return;

    try {
      const settingsRef = doc(this.firestore, 'website_settings', currentDomain);
      const settingsSnap = await getDoc(settingsRef);
      
      if (settingsSnap.exists()) {
        const settings = settingsSnap.data();
        this.theming = settings['theming']?.navigation || null;
      }
    } catch (error) {
      console.error('Error loading theming:', error);
    }
  }

  getMenuStyles(): { [key: string]: string } {
    if (!this.theming) return {};
    
    const padding = this.theming.padding || 16;
    const marginLeft = this.theming.marginLeft || 0;
    const marginRight = this.theming.marginRight || 0;
    
    const styles: { [key: string]: string } = {
      'background-color': this.theming.backgroundColor || '#1a1a1a',
      'padding': `${padding}px ${marginRight}px ${padding}px ${marginLeft}px`,
      'height': `${this.theming.height || 64}px`,
      'min-height': `${this.theming.height || 64}px`
    };

    if (this.theming.borderEnabled) {
      const borderWidth = this.theming.borderWidth || 0;
      if (borderWidth > 0) {
        styles['border'] = `${borderWidth}px ${this.theming.borderStyle || 'solid'} ${this.theming.borderColor || '#000000'}`;
      }
    }

    return styles;
  }

  getMenuClasses(): string {
    if (!this.theming) return '';
    
    const classes: string[] = [];
    
    // Menu position
    if (this.theming.menuPosition === 'center') {
      classes.push('justify-center');
    } else if (this.theming.menuPosition === 'right') {
      classes.push('justify-end');
    } else {
      classes.push('justify-start');
    }
    
    return classes.join(' ');
  }

  getLogoClasses(): string {
    if (!this.theming) return '';
    
    const classes: string[] = [];
    
    if (this.theming.logoPosition === 'center') {
      classes.push('mx-auto');
      classes.push('mr-0');
    } else if (this.theming.logoPosition === 'right') {
      classes.push('ml-auto');
      classes.push('mr-0');
    } else if (this.theming.logoPosition === 'separate') {
      classes.push('order-first');
    }
    
    return classes.join(' ');
  }

  isVisible(roles: string[]): boolean {
    return this.authService.hasAnyRole(roles);
  }

  logout(): void {
    this.authService.logout();
  }

  async onDomainChange() {
    await this.websiteService.setCurrentDomain(this.selectedDomain);
  }

  openAddDomainModal() {
    this.showAddDomainModal = true;
    this.newDomainName = '';
  }

  closeAddDomainModal() {
    this.showAddDomainModal = false;
    this.newDomainName = '';
    this.isSubmitting = false;
  }

  async addNewDomain() {
    if (!this.newDomainName.trim()) return;
    
    try {
      this.isSubmitting = true;
      await this.websiteService.addDomain(this.newDomainName.trim());
      // Refresh domains list
      this.domains = await this.websiteService.getDomains();
      this.selectedDomain = this.newDomainName.trim();
      this.onDomainChange();
      console.log('Domain added:', this.newDomainName);
      
      // Close modal and reset form
      this.closeAddDomainModal();
      
      // Auto refresh the page
      window.location.reload();
    } catch (error) {
      console.error('Error adding domain:', error);
      this.isSubmitting = false;
    }
  }

  openDomainSelectionModal() {
    this.showDomainSelectionModal = true;
  }

  closeDomainSelectionModal() {
    this.showDomainSelectionModal = false;
  }

  selectDomain(domain: Domain) {
    this.selectedDomain = domain.name;
    this.onDomainChange();
    this.closeDomainSelectionModal();
  }

  confirmDeleteDomain(domain: Domain, event: Event) {
    event.stopPropagation(); // Prevent domain selection when clicking delete
    this.domainToDelete = domain;
    this.showDeleteConfirmModal = true;
  }

  cancelDeleteDomain() {
    this.showDeleteConfirmModal = false;
    this.domainToDelete = null;
  }

  async deleteDomain() {
    if (!this.domainToDelete) return;
    
    try {
      await this.websiteService.deleteDomain(this.domainToDelete.id);
      // Refresh domains list
      this.domains = await this.websiteService.getDomains();
      
      // Close modals
      this.showDeleteConfirmModal = false;
      this.domainToDelete = null;
      
      // If we deleted the current domain, the service will handle switching
      // Reload to ensure everything is in sync
      window.location.reload();
    } catch (error) {
      console.error('Error deleting domain:', error);
      this.showDeleteConfirmModal = false;
      this.domainToDelete = null;
    }
  }
} 